'use client';

import type { JSX, ReactNode } from 'react';
import { useId } from 'react';
import { Radio as RadioPrimitive } from '@base-ui/react/radio';
import { RadioGroup as RadioGroupPrimitive } from '@base-ui/react/radio-group';
import { cn } from '@savvy-kit/lib/utils';

import { Label } from '../label/label';

const LIST_CLASS = 'inline-flex w-fit items-center gap-1 rounded-full bg-muted p-1 data-[disabled]:opacity-50';

const ITEM_CLASS =
  "inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium whitespace-nowrap text-secondary transition-colors outline-none select-none hover:text-dark focus-visible:ring-2 focus-visible:ring-border-light data-checked:bg-card data-checked:text-dark data-checked:shadow-sm data-[disabled]:pointer-events-none data-[disabled]:cursor-default data-[disabled]:text-disabled [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

type SegmentedFormControlOption = {
  readonly label: ReactNode;
  readonly value: string;
  readonly disabled?: boolean;
};

type SegmentedFormControlProps = Omit<
  RadioGroupPrimitive.Props<string>,
  'onValueChange' | 'className' | 'children' | 'render'
> & {
  readonly options: readonly SegmentedFormControlOption[];
  readonly label: string;
  readonly hideLabel?: boolean;
  readonly onValueChange?: (value: string) => void;
  readonly className?: string;
  readonly listClassName?: string;
  readonly itemClassName?: string;
};

function SegmentedFormControl({
  options,
  label,
  required = false,
  hideLabel = false,
  disabled = false,
  value,
  defaultValue,
  onValueChange,
  id,
  className,
  listClassName,
  itemClassName,
  ...props
}: SegmentedFormControlProps): JSX.Element {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const labelId = `${controlId}-label`;

  const resolvedDefaultValue = value === undefined && defaultValue === undefined ? options[0]?.value : defaultValue;

  return (
    <div data-slot="segmented-form-control" className={cn('flex flex-col gap-1', className)}>
      <Label
        id={labelId}
        className={cn('gap-1 text-xs font-normal text-secondary', hideLabel && 'sr-only', disabled && 'opacity-50')}
      >
        {label}
        {required ? (
          <span aria-hidden className="text-destructive">
            *
          </span>
        ) : null}
      </Label>
      <RadioGroupPrimitive
        {...props}
        id={controlId}
        data-slot="segmented-form-control-list"
        value={value}
        defaultValue={resolvedDefaultValue}
        onValueChange={onValueChange}
        disabled={disabled || undefined}
        required={required}
        aria-labelledby={labelId}
        className={cn(LIST_CLASS, listClassName)}
      >
        {options.map((option) => (
          <RadioPrimitive.Root
            key={option.value}
            data-slot="segmented-form-control-item"
            value={option.value}
            disabled={option.disabled}
            className={cn(ITEM_CLASS, itemClassName)}
          >
            {option.label}
          </RadioPrimitive.Root>
        ))}
      </RadioGroupPrimitive>
    </div>
  );
}

export { SegmentedFormControl };
export type { SegmentedFormControlOption, SegmentedFormControlProps };
